import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { InputComponent } from 'src/app/components/shared/input';
import { CategoryService } from 'src/app/services/category';
import { LocaleService } from 'src/app/services/locale';

@Component({
  selector: 'app-category1-question1',
  imports: [CommonModule, ReactiveFormsModule, InputComponent],
  template: `
    <div class="text-sm sm:text-base text-[#616161]">{{ strings[language].opt }}</div>
    <br />
    <div class="flex flex-wrap gap-4 sm:gap-6">
      <div
        (click)="setAnnual(true)"
        class="flex gap-2 align-middle items-center cursor-pointer text-sm sm:text-base"
      >
        <div
          [ngClass]="{ 'bg-[#183A25]': annual }"
          class="w-4 h-4 border border-gray-500 rounded-full flex-shrink-0"
        ></div>
        <div>{{ strings[language].annual }}</div>
      </div>
      <div
        (click)="setAnnual(false)"
        class="flex gap-2 align-middle items-center cursor-pointer text-sm sm:text-base"
      >
        <div
          [ngClass]="{
            'bg-[#183A25]': !annual
          }"
          class="w-4 h-4 border border-gray-500 rounded-full flex-shrink-0"
        ></div>
        <div>{{ strings[language].monthly }}</div>
      </div>
    </div>
    <form *ngIf="annual" [formGroup]="form">
      <div
        class="w-full bg-white flex flex-col px-4 sm:px-5 py-2 shadow-md drop-shadow-md rounded-lg mt-4 my-2"
      >
        <div class="flex flex-col sm:flex-row sm:justify-between align-middle sm:items-center gap-2 sm:gap-4 my-2">
          <div class="flex gap-3 sm:gap-4 align-middle items-center">
            <div class="text-[14px]">{{ strings[language].heading }}</div>
          </div>
          <div class="w-full sm:w-32">
            <app-input
              [keyUp]="onAnnualChange"
              type="number"
              [dollar]="false"
              unit="m³"
              placeholder="0.00"
              [control]="form.controls['ann']"
            ></app-input>
          </div>
        </div>
      </div>
    </form>
    <form *ngIf="!annual" [formGroup]="form">
      <div
        class="w-full bg-white flex flex-col px-4 sm:px-5 py-2 shadow-md drop-shadow-md rounded-lg mt-4 my-2"
      >
        <div
          *ngFor="let month of months; let x = index"
          class="flex flex-col sm:flex-row sm:justify-between align-middle sm:items-center gap-2 sm:gap-4 my-2"
        >
          <div class="flex gap-3 sm:gap-4 align-middle items-center">
            <div
              class="w-3 h-3 sm:w-4 sm:h-4 rounded-lg flex-shrink-0"
              [ngStyle]="{ background: month.color, opacity: 0.5 }"
            ></div>
            <div class="text-[14px]">{{ month.title }}</div>
          </div>
          <div class="w-full sm:w-32">
            <app-input
              [index]="x"
              [keyUp]="onMonthlyChange"
              type="number"
              [dollar]="false"
              unit="m³"
              placeholder="0.00"
              [control]="month.control"
            ></app-input>
          </div>
        </div>
      </div>
    </form>
  `
})
export class Category1Question1Component {
  language = inject(LocaleService).getLanguage();
  categoryService = inject(CategoryService);
  categories = this.categoryService.categories();

  annual: boolean = true;
  hasManualMonthlyEntries: boolean = false;

  strings: any = {
    english: {
      annual: 'Annual',
      monthly: 'Monthly',
      heading: 'Annual amount of gas consumption (m³)',
      jan: 'January',
      feb: 'February',
      mar: 'March',
      apr: 'April',
      may: 'May',
      jun: 'June',
      jul: 'July',
      aug: 'August',
      sep: 'September',
      oct: 'October',
      nov: 'November',
      dec: 'December',
      opt: 'Select the option for reporting'
    },
    italian: {
      annual: 'Annuale',
      monthly: 'Mensile',
      heading: 'Quantità annuale di consumo di gas (m³)',
      jan: 'Gennaio',
      feb: 'Febbraio',
      mar: 'Marzo',
      apr: 'Aprile',
      may: 'Maggio',
      jun: 'Giugno',
      jul: 'Luglio',
      aug: 'Agosto',
      sep: 'Settembre',
      oct: 'Ottobre',
      nov: 'Novembre',
      dec: 'Dicembre',
      opt: "Seleziona l'opzione per la rendicontazione"
    }
  };

  months = [
    {
      color: 'red',
      title: this.strings[this.language].jan,
      control: new FormControl('')
    },
    {
      color: 'blue',
      title: this.strings[this.language].feb,
      control: new FormControl('')
    },
    {
      color: 'green',
      title: this.strings[this.language].mar,
      control: new FormControl('')
    },
    {
      color: 'red',
      title: this.strings[this.language].apr,
      control: new FormControl('')
    },
    {
      color: 'blue',
      title: this.strings[this.language].may,
      control: new FormControl('')
    },
    {
      color: 'green',
      title: this.strings[this.language].jun,
      control: new FormControl('')
    },
    {
      color: 'red',
      title: this.strings[this.language].jul,
      control: new FormControl('')
    },
    {
      color: 'blue',
      title: this.strings[this.language].aug,
      control: new FormControl('')
    },
    {
      color: 'green',
      title: this.strings[this.language].sep,
      control: new FormControl('')
    },
    {
      color: 'red',
      title: this.strings[this.language].oct,
      control: new FormControl('')
    },
    {
      color: 'blue',
      title: this.strings[this.language].nov,
      control: new FormControl('')
    },
    {
      color: 'green',
      title: this.strings[this.language].dec,
      control: new FormControl('')
    }
  ];

  form = new FormGroup({
    ann: new FormControl(''),
    jan: this.months[0].control,
    feb: this.months[1].control,
    mar: this.months[2].control,
    apr: this.months[3].control,
    may: this.months[4].control,
    jun: this.months[5].control,
    jul: this.months[6].control,
    aug: this.months[7].control,
    sep: this.months[8].control,
    oct: this.months[9].control,
    nov: this.months[10].control,
    dec: this.months[11].control
  });

  ngOnInit() {
    this.loadSavedData();

    // Track manual monthly entries
    this.months.forEach(month => {
      month.control.valueChanges.subscribe(() => {
        if (!this.annual) {
          this.hasManualMonthlyEntries = true;
        }
      });
    });
  }

  loadSavedData() {
    const savedCategories = localStorage.getItem('categories');
    if (savedCategories) {
      try {
        const categories = JSON.parse(savedCategories);
        if (categories?.[0]?.questions?.[0]?.answers) {
          const answers = categories[0].questions[0].answers;

          // Check if there are manual monthly entries
          const hasManualEntries = this.months.some(m => {
            const monthKey = m.title.toLowerCase().substring(0, 3);
            return answers[monthKey] && parseFloat(answers[monthKey]) !== parseFloat(answers.ann || '0') / 12;
          });

          this.hasManualMonthlyEntries = hasManualEntries;
          this.annual = !hasManualEntries && !this.months.some(m => {
            const monthKey = m.title.toLowerCase().substring(0, 3);
            return answers[monthKey];
          });

          if (answers.ann) {
            this.form.controls.ann.setValue(answers.ann);

            if (!this.hasManualMonthlyEntries) {
              this.distributeAnnualToMonths(parseFloat(answers.ann));
            } else {
              // Load manual monthly values
              this.months.forEach(month => {
                const monthKey = month.title.toLowerCase().substring(0, 3);
                month.control.setValue(answers[monthKey] || '');
              });
            }
          }
        }
      } catch (e) {
        console.error('Error loading saved data', e);
      }
    }
  }

  setAnnual(isAnnual: boolean) {
    if (isAnnual !== this.annual) {
      this.annual = isAnnual;

      if (this.annual) {
        // When switching to annual, calculate sum of months
        this.calculateAnnualFromMonths();
      } else if (!this.hasManualMonthlyEntries) {
        // When switching to monthly, distribute annual value only if no manual entries exist
        const annualValue = parseFloat(this.form.controls.ann.value || '0');
        if (annualValue > 0) {
          this.distributeAnnualToMonths(annualValue);
        }
      }
      this.onChange();
    }
  }

  onAnnualChange = () => {
    const annualValue = parseFloat(this.form.controls.ann.value || '0');
    if (annualValue > 0 && this.annual) {
      this.distributeAnnualToMonths(annualValue);
    }
    this.onChange();
  };

  onMonthlyChange = () => {
    if (!this.annual) {
      this.hasManualMonthlyEntries = true;
      this.calculateAnnualFromMonths();
    }
    this.onChange();
  };

  distributeAnnualToMonths(annualValue: number) {
    const monthlyValue = (annualValue / 12).toFixed(2);
    this.months.forEach(month => {
      month.control.setValue(monthlyValue);
    });
    this.hasManualMonthlyEntries = false;
  }

  calculateAnnualFromMonths() {
    const sum = this.months.reduce((total, month) => {
      return total + (parseFloat(month.control.value || '0') || 0);
    }, 0);
    this.form.controls.ann.setValue(sum.toFixed(2));
  }

  onChange = () => {
    // Prepare the answers object
    const answers: any = { ann: this.form.controls.ann.value };

    if (!this.annual || this.hasManualMonthlyEntries) {
      this.months.forEach(month => {
        const monthKey = month.title.toLowerCase().substring(0, 3);
        answers[monthKey] = month.control.value;
      });
    }

    // Update the categories and save
    this.categories[0].questions[0].answers = answers;
    this.categoryService.persistCategories(this.categories);
  };
}
